/**
 * VerticalPage — SEO landing page per vertical (Roofing / HVAC / Solar / …)
 * Route: /v/:key
 *
 * Each vertical gets a tailored H1, sub, CTA (pre-fill category/business type
 * in /), proof stats, FAQ and specific social-proof. Shares the landing
 * design system (ivory background, Cabinet Grotesk, gold CTA).
 *
 * Intentionally static content — grown organically when SEO signals show which
 * verticals rank.
 */
import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Sparkles, Building, HardHat, Hammer, ClipboardCheck, Zap } from "lucide-react";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import { useLang } from "../i18n/LangContext";

const LOGO_URL = "/logos/agentelead-pro-dark-bg.png";

// ----------------------------------------------------------------------------
// Per-vertical content (PT / EN / ES)
// ----------------------------------------------------------------------------
const VERTICALS = {
  roofing: {
    icon: HardHat, imgKey: "roofing",
    color: "#0A1233",
    img: "https://images.pexels.com/photos/209235/pexels-photo-209235.jpeg?auto=compress&cs=tinysrgb&w=1600",
    pt: { name: "Roofing", niche: "telhadistas", city: "sua cidade",
          h1: "Leads prontos para fechar telhados antes do próximo furacão.",
          sub: "Empresas de roofing com 20+ anos de fila de orçamento. Encontre as que ainda não estão no Google Ads — e ofereça sua agência.",
          bullets: ["Filtragem por reviews ≥4.2 e 50+ fotos publicadas", "Detecção de empresas SEM site WordPress moderno", "Ad Library check: descubra quem NÃO anuncia no Meta"],
          proof: ["~8× ROAS em outreach para roofing (FL/TX/CA)", "180k+ empresas mapeadas", "Resposta em <5 min com Top Intent Now"] },
    en: { name: "Roofing", niche: "roofers", city: "your city",
          h1: "Close roofing deals before the next storm season.",
          sub: "Roofers with 20+ year backlogs. Spot the ones still NOT running Google Ads — and pitch your agency before they wake up.",
          bullets: ["Filter by reviews ≥4.2 and 50+ published photos", "Detect companies WITHOUT a modern WordPress site", "Ad Library check: see who's NOT advertising on Meta"],
          proof: ["~8× ROAS on roofing outreach (FL/TX/CA)", "180k+ businesses mapped", "Reply in <5 min with Top Intent Now"] },
    es: { name: "Techos", niche: "techadores", city: "tu ciudad",
          h1: "Cierra contratos de techos antes de la próxima tormenta.",
          sub: "Empresas de techos con 20+ años de pedidos pendientes. Detecta las que NO tienen Google Ads — y ofrece tu agencia antes que despierten.",
          bullets: ["Filtro por reviews ≥4.2 y 50+ fotos publicadas", "Detecta empresas SIN un sitio WordPress moderno", "Ad Library: mira quién NO anuncia en Meta"],
          proof: ["~8× ROAS en outreach de techos (FL/TX/CA)", "180k+ negocios mapeados", "Responde en <5 min con Top Intent Now"] },
  },
  hvac: {
    icon: Hammer, imgKey: "hvac",
    color: "#0A3A7A",
    img: "https://images.pexels.com/photos/4108715/pexels-photo-4108715.jpeg?auto=compress&cs=tinysrgb&w=1600",
    pt: { name: "HVAC", niche: "HVAC", city: "sua cidade",
          h1: "Ar-condicionado é sazonal. Sua pipeline não deveria ser.",
          sub: "Empresas de HVAC faturam 70% entre maio e setembro. Monte a pipeline dos outros 7 meses com leads B2C intent e manutenção recorrente.",
          bullets: ["Monitor B2C Intent: 'meu ar não gela', 'orçamento AC split'", "Contas comerciais sem contrato de manutenção", "Score por idade do AC (Google Street View + patterns)"],
          proof: ["110k+ prestadores HVAC mapeados", "3 de 10 leads MORNO viram WON em 14 dias", "Outreach multi-canal: email + WhatsApp + SMS"] },
    en: { name: "HVAC", niche: "HVAC", city: "your city",
          h1: "HVAC is seasonal. Your pipeline shouldn't be.",
          sub: "HVAC shops book 70% of revenue May–September. Fill the other 7 months with B2C intent leads and recurring maintenance accounts.",
          bullets: ["B2C Intent monitor: 'my AC won't cool', 'AC split quote'", "Commercial accounts with no maintenance contract", "Score by AC age (Google Street View + patterns)"],
          proof: ["110k+ HVAC businesses mapped", "3 of 10 WARM leads become WON in 14 days", "Multi-channel outreach: email + WhatsApp + SMS"] },
    es: { name: "HVAC", niche: "HVAC", city: "tu ciudad",
          h1: "HVAC es estacional. Tu pipeline no debería serlo.",
          sub: "Los talleres HVAC facturan 70% entre mayo y septiembre. Llena los otros 7 meses con leads B2C intent y cuentas de mantenimiento.",
          bullets: ["Monitor B2C Intent: 'mi AC no enfría', 'cotización split'", "Cuentas comerciales sin contrato de mantenimiento", "Score por edad del AC (Google Street View + patterns)"],
          proof: ["110k+ empresas HVAC mapeadas", "3 de 10 leads WARM pasan a WON en 14 días", "Outreach multi-canal: email + WhatsApp + SMS"] },
  },
  solar: {
    icon: Sparkles, imgKey: "solar",
    color: "#1F5F3F",
    img: "https://images.pexels.com/photos/356036/pexels-photo-356036.jpeg?auto=compress&cs=tinysrgb&w=1600",
    pt: { name: "Solar", niche: "solar", city: "sua cidade",
          h1: "Feche instalações solar enquanto o crédito ainda é barato.",
          sub: "Empresas de solar são a vertical de maior CAC nos EUA. Encurte o ciclo com leads pré-auditados por GPT.",
          bullets: ["Detecção de telhados grandes via Google Places", "Imóveis 4+ dormitórios + renda acima da média", "B2C Intent: 'conta de luz subiu', 'painel solar vale a pena'"],
          proof: ["CAC médio 42% menor vs Meta Ads direto", "Conversão 6% em leads QUENTE", "Nurture automático por 14 dias"] },
    en: { name: "Solar", niche: "solar", city: "your city",
          h1: "Close solar deals while credits are still cheap.",
          sub: "Solar is the highest-CAC vertical in the US. Shorten the cycle with GPT pre-audited leads.",
          bullets: ["Large-roof detection via Google Places", "4+ bedroom homes with above-average income", "B2C Intent: 'electric bill jumped', 'is solar worth it'"],
          proof: ["Avg CAC 42% lower than direct Meta Ads", "6% conversion on HOT leads", "Auto nurture for 14 days"] },
    es: { name: "Solar", niche: "solar", city: "tu ciudad",
          h1: "Cierra instalaciones solares antes que suban los créditos.",
          sub: "Solar es la vertical con CAC más alto en EE.UU. Acorta el ciclo con leads pre-auditados por GPT.",
          bullets: ["Detección de techos grandes vía Google Places", "Casas 4+ habitaciones + ingresos sobre la media", "B2C Intent: 'subió la luz', '¿vale la pena solar?'"],
          proof: ["CAC promedio 42% menor vs Meta Ads directo", "6% conversión en leads HOT", "Nurture automático de 14 días"] },
  },
  plumbing: {
    icon: ClipboardCheck, imgKey: "plumbing",
    color: "#0E4E6E",
    img: "https://images.pexels.com/photos/8486936/pexels-photo-8486936.jpeg?auto=compress&cs=tinysrgb&w=1600",
    pt: { name: "Encanamento", niche: "encanadores", city: "sua cidade",
          h1: "Plumbing não espera. A prospecção também não precisa.",
          sub: "Emergências de encanamento têm janela de 30 min. Seu funil precisa capturar, qualificar e responder mais rápido que o concorrente.",
          bullets: ["Top Intent Now real-time em Reddit/HN/FB groups", "Leads com reviews baixas = oportunidade de agência local", "Outreach com templates de urgência testados"],
          proof: ["85k+ encanadores mapeados", "Resposta média: 4min", "Taxa de resposta 28% no primeiro toque"] },
    en: { name: "Plumbing", niche: "plumbers", city: "your city",
          h1: "Plumbing doesn't wait. Your prospecting doesn't have to.",
          sub: "Plumbing emergencies have a 30-minute window. Your funnel needs to capture, qualify, and reply faster than the competition.",
          bullets: ["Real-time Top Intent Now on Reddit/HN/FB groups", "Low-review leads = local agency opportunity", "Urgency-tested outreach templates"],
          proof: ["85k+ plumbers mapped", "4-minute average response", "28% reply rate on first outreach"] },
    es: { name: "Plomería", niche: "plomeros", city: "tu ciudad",
          h1: "La plomería no espera. Tu prospección tampoco.",
          sub: "Las emergencias de plomería tienen ventana de 30 min. Tu funnel necesita capturar, calificar y responder más rápido que la competencia.",
          bullets: ["Top Intent Now en tiempo real en Reddit/HN/FB groups", "Leads con reviews bajas = oportunidad de agencia local", "Plantillas de outreach probadas en urgencia"],
          proof: ["85k+ plomeros mapeados", "Respuesta promedio: 4 min", "28% de respuesta en primer contacto"] },
  },
  gc: {
    icon: Building, imgKey: "gc",
    color: "#C0853A",
    img: "https://images.pexels.com/photos/159306/construction-site-build-construction-work-159306.jpeg?auto=compress&cs=tinysrgb&w=1600",
    pt: { name: "Construção Geral", niche: "GCs", city: "sua cidade",
          h1: "General Contractors precisam ser achados — e você precisa deles.",
          sub: "GCs controlam o orçamento. Se você vende material, sub-empreitada ou serviços para a obra, a pipeline começa aqui.",
          bullets: ["Base Public Works integrada (bids abertos)", "Filtro por CNPJ ativo + 3 anos de mercado", "SAM.gov + bidnet: contratos federais em tempo real"],
          proof: ["42k+ GCs ativos (US)", "Ticket médio $180k", "Integração direta com Kanban multi-stage"] },
    en: { name: "General Contractor", niche: "GCs", city: "your city",
          h1: "General Contractors need to be found — and you need them.",
          sub: "GCs hold the budget. If you sell materials, subcontracting, or jobsite services, your pipeline starts here.",
          bullets: ["Public Works database integrated (open bids)", "Filter by active license + 3+ years in market", "SAM.gov + bidnet: real-time federal contracts"],
          proof: ["42k+ active US GCs", "Avg ticket $180k", "Direct Kanban multi-stage integration"] },
    es: { name: "Contratista General", niche: "GCs", city: "tu ciudad",
          h1: "Los General Contractors necesitan ser encontrados — y tú los necesitas.",
          sub: "Los GCs controlan el presupuesto. Si vendes materiales, subcontratos o servicios de obra, tu pipeline empieza aquí.",
          bullets: ["Base de Public Works integrada (bids abiertos)", "Filtro por licencia activa + 3+ años de mercado", "SAM.gov + bidnet: contratos federales en tiempo real"],
          proof: ["42k+ GCs activos (EE.UU.)", "Ticket promedio $180k", "Integración directa con Kanban multi-etapa"] },
  },
  elect: {
    icon: Zap, imgKey: "elect",
    color: "#B9860B",
    img: "https://images.pexels.com/photos/257736/pexels-photo-257736.jpeg?auto=compress&cs=tinysrgb&w=1600",
    pt: { name: "Elétrica", niche: "elétrica", city: "sua cidade",
          h1: "Eletricistas certificados que ainda fazem prospecção manual.",
          sub: "87% dos eletricistas ainda dependem de indicação. Oferecer SEO local + Google Business é a venda óbvia.",
          bullets: ["Detecção por CREA/ICE ativo", "Filtro: sem site OU site WordPress antigo", "Top Intent Now: pedidos B2C para instalação residencial"],
          proof: ["62k+ eletricistas mapeados", "Ticket médio de serviço de agência: $2.4k/mês", "Outreach responsivo a múltiplos idiomas"] },
    en: { name: "Electrical", niche: "electricians", city: "your city",
          h1: "Licensed electricians still doing prospecting by hand.",
          sub: "87% of electricians still depend on referrals. Offering local SEO + Google Business is the obvious pitch.",
          bullets: ["Detect by active ICC/ECA license", "Filter: no website OR aged WordPress", "Top Intent Now: residential install B2C requests"],
          proof: ["62k+ electricians mapped", "Agency ticket avg $2.4k/month", "Multi-language responsive outreach"] },
    es: { name: "Eléctricos", niche: "eléctricos", city: "tu ciudad",
          h1: "Electricistas certificados que aún prospectan a mano.",
          sub: "87% de los electricistas aún dependen de referidos. Ofrecer SEO local + Google Business es la venta obvia.",
          bullets: ["Detección por licencia activa ICE/CE", "Filtro: sin sitio O WordPress antiguo", "Top Intent Now: pedidos B2C de instalación residencial"],
          proof: ["62k+ electricistas mapeados", "Ticket promedio de agencia: $2.4k/mes", "Outreach multi-idioma responsivo"] },
  },
};

const FAQ_COPY = {
  pt: {
    title: "Perguntas frequentes",
    items: [
      ["Quantos leads consigo em {city}?", "A plataforma mapeia 100k+ empresas por vertical. Um search médio retorna 50-300 resultados filtrados por reviews, website e presença social."],
      ["Como vocês se comparam ao ZoomInfo/Apollo?", "ZoomInfo é B2B-first global. Nós somos hiper-local: Google Maps + reviews + auditoria em tempo real. Melhor custo-benefício para agências e prestadores."],
      ["Tem trial grátis?", "Sim. 14 dias free trial sem cartão de crédito. Entrou e começou."],
    ],
  },
  en: {
    title: "Frequently asked questions",
    items: [
      ["How many leads do I get in {city}?", "The platform maps 100k+ businesses per vertical. A typical search returns 50–300 results filtered by reviews, website and social presence."],
      ["How do you compare to ZoomInfo / Apollo?", "ZoomInfo is global B2B-first. We're hyper-local: Google Maps + reviews + real-time audit. Better bang-for-buck for agencies and service providers."],
      ["Free trial?", "Yes — 14-day free trial, no credit card required. Sign up and start."],
    ],
  },
  es: {
    title: "Preguntas frecuentes",
    items: [
      ["¿Cuántos leads obtengo en {city}?", "La plataforma mapea 100k+ empresas por vertical. Una búsqueda típica devuelve 50–300 resultados filtrados por reviews, sitio y presencia social."],
      ["¿Cómo se comparan con ZoomInfo/Apollo?", "ZoomInfo es global B2B-first. Somos hiper-local: Google Maps + reviews + auditoría en tiempo real. Mejor costo-beneficio para agencias y prestadores."],
      ["¿Hay trial gratis?", "Sí. 14 días gratis sin tarjeta de crédito. Entraste y empezaste."],
    ],
  },
};

const TRY_CTA = { pt: "Começar FREE TRIAL", en: "Start FREE TRIAL", es: "Empezar FREE TRIAL" };
const SECONDARY_CTA = { pt: "Ver plataforma →", en: "See the platform →", es: "Ver plataforma →" };
const BACK_LINK = { pt: "← Voltar para o site", en: "← Back to site", es: "← Volver al sitio" };

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5, ease: "easeOut" } }),
};

export default function VerticalPage() {
  const { key } = useParams();
  const { lang, setLang } = useLang();
  const v = VERTICALS[key];

  useEffect(() => {
    if (v) {
      const title = `${v[lang]?.name || key} · AgenteLead Pro`;
      document.title = title;
    }
  }, [key, lang, v]);

  if (!v) return <Navigate to="/" replace />;

  const c = v[lang] || v.en;
  const Icon = v.icon;
  const faq = FAQ_COPY[lang] || FAQ_COPY.en;

  return (
    <div data-testid={`vertical-page-${key}`} className="min-h-screen" style={{ background: "#FBF8F0", color: "#0A1233", fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b" style={{ backgroundColor: "#FBF8F0F0", backdropFilter: "blur(12px)", borderColor: "#E5E2DA" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="AgenteLead Pro" className="h-9 w-9 object-contain" />
            <span className="font-display text-[17px] font-extrabold tracking-tight">AgenteLead <span style={{ color: "#C28840" }}>Pro</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <LangToggle />
            <Link to="/login" className="hidden md:inline-flex h-10 items-center px-4 rounded-lg border font-mono-tech text-[11px] uppercase tracking-[0.18em]" style={{ borderColor: "#0A1233", color: "#0A1233" }}>{lang === "pt" ? "Entrar" : lang === "es" ? "Entrar" : "Sign in"}</Link>
            <Link to="/login"
              data-testid="vertical-cta-top"
              className="h-10 inline-flex items-center px-5 rounded-lg font-mono-tech text-[11px] uppercase tracking-[0.18em] font-bold text-white" style={{ background: "#E5C572", color: "#0A1233" }}>{TRY_CTA[lang]}</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-24 grid lg:grid-cols-5 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="lg:col-span-3">
            <Link to="/#verticals" className="inline-flex items-center gap-1.5 text-[12px] font-mono-tech uppercase tracking-[0.22em]" style={{ color: "#64748B" }}>
              {BACK_LINK[lang]}
            </Link>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ borderColor: v.color, color: v.color, background: `${v.color}14` }}>
              <Icon className="w-4 h-4" />
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] font-bold">{c.name}</span>
            </div>
            <h1 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight" style={{ color: "#0A1233" }}>
              {c.h1}
            </h1>
            <p className="mt-5 text-[15px] md:text-[17px] leading-relaxed max-w-xl" style={{ color: "#475569" }}>{c.sub}</p>
            <ul className="mt-6 space-y-2.5">
              {c.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-[14px]" style={{ color: "#0A1233" }}>
                  <Check className="w-4 h-4 mt-1 shrink-0" style={{ color: v.color }} /> {b}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login"
                data-testid="vertical-cta-hero"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-lg font-mono-tech text-[12px] uppercase tracking-[0.18em] font-bold" style={{ background: "#E5C572", color: "#0A1233" }}>
                {TRY_CTA[lang]} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-lg border font-mono-tech text-[12px] uppercase tracking-[0.18em] font-bold" style={{ borderColor: "#0A1233", color: "#0A1233" }}>
                {SECONDARY_CTA[lang]}
              </Link>
            </div>
          </motion.div>

          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={1} className="lg:col-span-2">
            <div className="relative rounded-2xl overflow-hidden shadow-xl ring-1" style={{ ringColor: "#E5E2DA" }}>
              <img src={v.img} alt={c.name} className="w-full h-[420px] object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1233]/80 via-[#0A1233]/20 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] opacity-80">{c.niche.toUpperCase()} · {c.city}</div>
                <div className="mt-1 font-display text-xl font-extrabold">{c.name}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Proof */}
      <section className="border-y" style={{ borderColor: "#E5E2DA", background: "#FFFFFF" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12 grid md:grid-cols-3 gap-8">
          {c.proof.map((p, i) => (
            <motion.div key={p} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
              className="text-center md:text-left">
              <div className="font-mono-tech text-[10.5px] uppercase tracking-[0.22em] mb-2" style={{ color: v.color }}>{["#01","#02","#03"][i]}</div>
              <p className="text-[15px] leading-relaxed font-semibold">{p}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <h2 className="font-display text-3xl font-black tracking-tight" style={{ color: "#0A1233" }}>{faq.title}</h2>
          <dl className="mt-8 space-y-5">
            {faq.items.map(([q, a]) => (
              <div key={q} className="rounded-xl border p-5" style={{ borderColor: "#E5E2DA", background: "#FFFFFF" }}>
                <dt className="font-display text-[16px] font-bold" style={{ color: "#0A1233" }}>{q.replace("{city}", c.city)}</dt>
                <dd className="mt-2 text-[13.5px] leading-relaxed" style={{ color: "#475569" }}>{a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t py-14" style={{ borderColor: "#E5E2DA", background: "#0A1233" }}>
        <div className="max-w-3xl mx-auto px-5 lg:px-10 text-center text-white">
          <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight">
            {lang === "pt" ? `Leads de ${c.name} prontos em 14 dias.` : lang === "es" ? `Leads de ${c.name} listos en 14 días.` : `${c.name} leads, ready in 14 days.`}
          </h3>
          <p className="mt-3 text-[14px] opacity-80 max-w-xl mx-auto">
            {lang === "pt" ? "Sem cartão. Sem config. Entrou, buscou e já tem contato pra abordar." : lang === "es" ? "Sin tarjeta. Sin config. Entraste, buscaste y ya tienes contactos." : "No card. No setup. Sign in, search, start closing."}
          </p>
          <Link to="/login"
            data-testid="vertical-cta-bottom"
            className="mt-7 inline-flex items-center gap-2 h-12 px-7 rounded-lg font-mono-tech text-[12px] uppercase tracking-[0.18em] font-bold" style={{ background: "#E5C572", color: "#0A1233" }}>
            {TRY_CTA[lang]} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
